import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, } from 'react';
import { Paper, Table, TableRow, TableCell, TableHead, Icon, TableBody, makeStyles, Button, Menu, MenuItem, Hidden, IconButton, } from '@material-ui/core';
import clsx from 'clsx';
import { createHeaderColumn, useModeTableStyle, useModePanelStyle, useDropdown, } from '../../../..';
import { createPreviewItemCell, createRemoveItemCell, } from '../../../componentUtils';
import { CompPanelHeader, } from '../../compPanelHeader';
const useStyle = makeStyles((theme) => {
    return {
        panelContent: {
            padding: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        },
        filter: {
            '&:not(:last-child)': {
                marginRight: theme.spacing(2),
            },
            '& .label': {
                display: 'flex',
                textTransform: 'none',
                flexDirection: 'column',
                alignItems: 'flex-start',
                '& .filter-label': {
                    fontWeight: 'normal',
                    fontSize: 'smaller',
                    color: theme.palette.text.secondary,
                    lineHeight: '1em',
                },
                '& .filter-value': {
                    color: theme.palette.text.primary,
                },
            },
        },
        tableContainer: {
            overflow: 'auto',
            flex: 1,
        },
        tableCol: {
            '&.id-col': {
                textAlign: 'center',
            },
            '&.deviceId-col': {
                textAlign: 'center',
            },
            '&.homeId-col': {
                textAlign: 'center',
            },
            '&.name-col': {
                textAlign: 'left',
                width: '100%',
                whiteSpace: 'normal',
            },
            '&.robotClass-col': {
                textAlign: 'left',
            },
            '&.overrideRobotDefinition-col': {
                textAlign: 'left',
                width: '100%',
            },
            '&.createdAt-col': {
                textAlign: 'left',
            },
            '&.updatedAt-col': {
                textAlign: 'left',
            },
            '&.availability-col': {
                textAlign: 'center',
                '&.available': {
                    '& .value': {
                        color: theme.palette.success.main,
                    },
                },
                '&.not-available': {
                    '& .value': {
                        color: theme.palette.error.main,
                    },
                },
            },
            '&.status-col': {
                textAlign: 'center',
                '& .value': {
                    fontSize: 'smaller',
                    fontWeight: 500,
                    padding: theme.spacing(0.3, 1),
                    textTransform: 'lowercase',
                    background: theme.palette.info.main,
                    color: theme.palette.info.contrastText,
                    minWidth: '5em',
                    maxWidth: '10em',
                    width: '100%',
                    display: 'inline-block',
                    borderRadius: 20,
                },
                // unknown
                '&.status--1': {
                    '& .value': {
                        background: '#bbbbbb',
                    },
                },
                // normal
                '&.status-0': {
                    '& .value': {
                        background: theme.palette.success.main,
                        color: theme.palette.success.contrastText,
                    },
                },
                // minor
                '&.status-1000': {
                    '& .value': {
                        background: theme.palette.info.main,
                        color: theme.palette.info.contrastText,
                    },
                },
                // warning
                '&.status-6000': {
                    '& .value': {
                        background: theme.palette.warning.main,
                        color: theme.palette.warning.contrastText,
                    },
                },
                // critical
                '&.status-11000': {
                    '& .value': {
                        background: theme.palette.error.main,
                        color: theme.palette.error.contrastText,
                    },
                },
            },
        },
        linkRobotButton: {
            marginLeft: theme.spacing(1),
        },
        addRobotButton: {
            marginLeft: theme.spacing(1),
        },
    };
}, {
    name: 'RobotsTable', index: 1,
});
/**
 * This is an implementation of RCBaseRobotsListComp which display robots in tabular form.
 */
export const RCRobotsTable = (props) => {
    var _a;
    const tableClasses = useModeTableStyle();
    const panelClasses = useModePanelStyle();
    const classes = useStyle();
    const [menuAnchorEl, isMenuOpened, onMenuAnchorElClicked, onMenuClosed] = useDropdown();
    const [robotClassFilterMenuAnchorEl, isRobotClassFilterMenuOpened, onRobotClassFilterAnchorElClicked, onRobotClassFilterMenuClosed] = useDropdown();
    /**
     * Get the label of the currently selected option so we can display the selected value in the dropdown
     */
    const currentRobotClassLabel = useMemo(() => {
        var _a;
        if ((_a = props.filters) === null || _a === void 0 ? void 0 : _a.robotClass) {
            const selectedOption = props.filters.robotClass.options.find((option) => {
                var _a, _b;
                return option.value === (((_b = (_a = props.filters) === null || _a === void 0 ? void 0 : _a.robotClass) === null || _b === void 0 ? void 0 : _b.currentValue) || '');
            });
            return (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.label) || '';
        }
        return '';
    }, [props.filters]);
    return (_jsxs(Paper, { elevation: 2, className: clsx(panelClasses.root, props.className, props.showCustomActionOnHover && 'show-custom-action-on-hover'), children: [_jsx(CompPanelHeader, { ...props, customContent: (_jsxs(_Fragment, { children: [((_a = props.filters) === null || _a === void 0 ? void 0 : _a.robotClass) && (_jsxs(_Fragment, { children: [_jsx(Hidden, { xsDown: true, children: _jsx(Button, { className: classes.filter, onClick: onRobotClassFilterAnchorElClicked, endIcon: _jsx(Icon, { children: "arrow_drop_down" }), children: _jsxs("div", { className: "label", children: [_jsx("div", { className: "filter-label", children: props.filters.robotClass.label }), _jsx("div", { className: "filter-value", children: currentRobotClassLabel })] }) }) }), _jsx(Hidden, { smUp: true, children: _jsx(IconButton, { className: classes.filter, onClick: onRobotClassFilterAnchorElClicked, children: _jsx(Icon, { children: "filter_list" }) }) }), _jsx(Menu, { getContentAnchorEl: null, anchorEl: robotClassFilterMenuAnchorEl, anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }, transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }, keepMounted: true, open: isRobotClassFilterMenuOpened, onClose: onRobotClassFilterMenuClosed, children: props.filters.robotClass.options.map((option) => {
                                        var _a, _b;
                                        return (_jsx(MenuItem, { selected: option.value === ((_b = (_a = props.filters) === null || _a === void 0 ? void 0 : _a.robotClass) === null || _b === void 0 ? void 0 : _b.currentValue), onClick: () => {
                                                var _a, _b;
                                                onRobotClassFilterMenuClosed();
                                                if ((_b = (_a = props.filters) === null || _a === void 0 ? void 0 : _a.robotClass) === null || _b === void 0 ? void 0 : _b.onChange) {
                                                    props.filters.robotClass.onChange(option.value);
                                                }
                                            }, children: option.label }, option.label));
                                    }) })] })), props.canLinkRobots && props.canCreateRobots && props.texts && (_jsxs(_Fragment, { children: [_jsx(Button, { className: classes.addRobotButton, size: "small", variant: "contained", color: "primary", startIcon: _jsx(Icon, { fontSize: "small", children: "add" }), onClick: (event) => {
                                        onMenuAnchorElClicked(event);
                                    }, children: props.texts.addButtonLabel }), _jsxs(Menu, { getContentAnchorEl: null, anchorEl: menuAnchorEl, anchorOrigin: {
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }, transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }, keepMounted: true, open: isMenuOpened, onClose: () => {
                                        onMenuClosed();
                                    }, children: [_jsx(MenuItem, { onClick: (event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                onMenuClosed();
                                                if (props.onLinkRobotClicked) {
                                                    props.onLinkRobotClicked();
                                                }
                                            }, children: props.texts.linkExistingRobotsButtonLabel }), _jsx(MenuItem, { onClick: (event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                onMenuClosed();
                                                if (props.onCreateNewRobotClicked) {
                                                    props.onCreateNewRobotClicked();
                                                }
                                            }, children: props.texts.createNewRobotButtonLabel })] })] })), props.canLinkRobots && !props.canCreateRobots && props.texts && (_jsx(Button, { className: classes.linkRobotButton, size: "small", color: "primary", variant: "contained", startIcon: _jsx(Icon, { fontSize: "small", children: "link" }), onClick: () => {
                                if (props.onLinkRobotClicked) {
                                    props.onLinkRobotClicked();
                                }
                            }, children: props.texts.linkButtonLabel })), props.canCreateRobots && !props.canLinkRobots && props.texts && (_jsx(Button, { className: classes.addRobotButton, size: "small", color: "primary", variant: "contained", startIcon: _jsx(Icon, { fontSize: "small", children: "add" }), onClick: () => {
                                if (props.onCreateNewRobotClicked) {
                                    props.onCreateNewRobotClicked();
                                }
                            }, children: props.texts.addButtonLabel }))] })) }), _jsxs("div", { className: clsx(panelClasses.panelContent, classes.panelContent), children: [props.listData && props.listData.length > 0 && (_jsxs(_Fragment, { children: [_jsxs("div", { className: classes.tableContainer, children: [_jsxs(Table, { className: tableClasses.root, size: "medium", children: [_jsx(TableHead, { className: tableClasses.tableHeader, children: _jsx(TableRow, { className: tableClasses.tableHeaderRow, children: Object.keys(props.fieldsSettings.fields).filter((fieldName) => {
                                                        var _a;
                                                        return !((_a = props.fieldsSettings.fields[fieldName]) === null || _a === void 0 ? void 0 : _a.hidden);
                                                    }).map((fieldName) => {
                                                        const field = props.fieldsSettings.fields[fieldName];
                                                        if (field && !field.hidden) {
                                                            return createHeaderColumn(props.fieldsSettings, fieldName, field, `${tableClasses.tableCol} ${classes.tableCol} ${fieldName}-col`);
                                                        }
                                                        return _jsx(_Fragment, {});
                                                    }) }) }), _jsx(TableBody, { children: props.listData.map((dataItem) => {
                                                    return (_jsx(TableRow, { className: clsx(tableClasses.tableBodyRow, 'clickable', dataItem.selected && 'selected'), onClick: () => {
                                                            if (props.onListItemSelected) {
                                                                props.onListItemSelected(dataItem.actualValue);
                                                            }
                                                        }, children: Object.keys(props.fieldsSettings.fields).filter((fieldName) => {
                                                            var _a;
                                                            return !((_a = props.fieldsSettings.fields[fieldName]) === null || _a === void 0 ? void 0 : _a.hidden);
                                                        }).map((fieldName) => {
                                                            if (fieldName === 'status' && props.fieldsSettings.fields.status
                                                                && !props.fieldsSettings.fields.status.hidden) {
                                                                return (_jsx(TableCell, { className: clsx(tableClasses.tableCol, classes.tableCol, 'status-col', `status-${dataItem.status}`), children: _jsx("div", { className: "value", children: dataItem.displayValue.status }) }, fieldName));
                                                            }
                                                            if (fieldName === 'availability' && props.fieldsSettings.fields.availability
                                                                && !props.fieldsSettings.fields.availability.hidden) {
                                                                return (_jsx(TableCell, { className: clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`, dataItem.displayValue.available && 'available', !dataItem.displayValue.available && 'not-available'), children: _jsx("div", { className: "value", children: dataItem.displayValue.available
                                                                            ? _jsx(Icon, { className: "not-available", children: "check" })
                                                                            : _jsx(Icon, { className: "available", children: "not_interested" }) }) }, fieldName));
                                                            }
                                                            if (fieldName === 'remove' && props.fieldsSettings.fields.remove
                                                                && !props.fieldsSettings.fields.remove.hidden) {
                                                                return (
                                                                // NOTE: dataItem.canBeRemoved must be true
                                                                createRemoveItemCell(dataItem, clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`), props.onRemoveItemClicked));
                                                            }
                                                            if (fieldName === 'preview' && props.fieldsSettings.fields.preview
                                                                && !props.fieldsSettings.fields.preview.hidden) {
                                                                return createPreviewItemCell(dataItem, clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`), props.onPreviewItemClicked);
                                                            }
                                                            /**
                                                             * Handle all the other fields that don't have special UI. These are the fields that
                                                             * display dataItem.displayValue as plain text
                                                             */
                                                            if (props.fieldsSettings.fields[fieldName]
                                                                && !props.fieldsSettings.fields[fieldName].hidden) {
                                                                return (_jsx(TableCell, { className: clsx(tableClasses.tableCol, classes.tableCol, `${fieldName}-col`), children: dataItem.displayValue[fieldName] }, fieldName));
                                                            }
                                                            return _jsx(_Fragment, {});
                                                        }) }, `${dataItem.actualValue.homeId}-${dataItem.actualValue.id}`));
                                                }) })] }), props.listData && props.listData.length === 0 && props.emptyDataMessageComp] }), props.paginationComp] })), !props.listData && props.noDataMessageComp] })] }));
};
